import { useState, useEffect, useCallback, useMemo } from 'react'
import { useTeam } from 'team/contexts/teamContext'
import { getTeamSubteams } from 'team/apis/teamSubteamApi'

/**
 * Custom hook to fetch and filter subteams
 * @returns {Object} { subteams, filteredSubteams, loading, fetchSubteams }
 */
export const useSubteams = (searchQuery = '') => {
    const { currentTeamId } = useTeam()
    const [subteams, setSubteams] = useState([])
    const [loading, setLoading] = useState(false)

    // Fetch subteams
    const fetchSubteams = useCallback(async () => {
        if (!currentTeamId) return

        try {
            setLoading(true)
            const response = await getTeamSubteams(currentTeamId)

            if (response && response.success && response.subteams) {
                // Sort subteams by creation date (oldest first, newest last)
                const sortedSubteams = [...response.subteams].sort((a, b) => {
                    const dateA = new Date(a.createdAt || a.created_at || 0)
                    const dateB = new Date(b.createdAt || b.created_at || 0)
                    return dateA - dateB // Ascending order (oldest to newest)
                })
                setSubteams(sortedSubteams)
            }
        } catch (error) {
            console.error('Error fetching subteams:', error)
        } finally {
            setLoading(false)
        }
    }, [currentTeamId])

    // Fetch subteams on mount and when teamId changes
    useEffect(() => {
        fetchSubteams()
    }, [fetchSubteams])

    // Filter subteams based on search query
    const filteredSubteams = useMemo(() => {
        return subteams.filter(subteam => {
            if (!searchQuery) return true
            const query = searchQuery.toLowerCase()
            return (
                subteam.name?.toLowerCase().includes(query) ||
                subteam.description?.toLowerCase().includes(query)
            )
        })
    }, [subteams, searchQuery])

    return {
        subteams,
        filteredSubteams,
        loading,
        fetchSubteams,
        setSubteams
    }
}

